/**
 * Utility functions for restaurant-scoped localStorage
 * All keys are prefixed with the restaurant slug to support multiple restaurants.
 * Values are ciphered (ooenc1.*) — see storageCipher.js.
 */

import {
  decryptStorageValue,
  encryptStorageValue,
} from './storageCipher';

/**
 * Get a restaurant-scoped localStorage key
 * @param {string} restaurantSlug - The restaurant slug
 * @param {string} key - The base key name
 * @returns {string} The scoped key
 */
export const getRestaurantStorageKey = (restaurantSlug, key) => {
  if (!restaurantSlug) {
    console.warn('getRestaurantStorageKey: restaurantSlug is required');
    return key; // Fallback to original key if no slug provided
  }
  return `restaurant_${restaurantSlug}_${key}`;
};

/**
 * Get item from restaurant-scoped localStorage
 * @param {string} restaurantSlug - The restaurant slug
 * @param {string} key - The base key name
 * @returns {any} The parsed value or null
 */
export const getRestaurantStorageItem = (restaurantSlug, key) => {
  if (!restaurantSlug) {
    return null;
  }
  try {
    const scopedKey = getRestaurantStorageKey(restaurantSlug, key);
    const item = localStorage.getItem(scopedKey);
    if (item == null) return null;
    const value = decryptStorageValue(item);
    // Migrate legacy plain JSON → cipher
    if (value != null && !String(item).startsWith('ooenc1.')) {
      try {
        localStorage.setItem(scopedKey, encryptStorageValue(value));
      } catch (_) {
        /* ignore */
      }
    }
    return value;
  } catch (error) {
    console.error(`Error getting restaurant storage item for ${key}:`, error);
    return null;
  }
};

/**
 * Set item in restaurant-scoped localStorage
 * @param {string} restaurantSlug - The restaurant slug
 * @param {string} key - The base key name
 * @param {any} value - The value to store
 */
export const setRestaurantStorageItem = (restaurantSlug, key, value) => {
  if (!restaurantSlug) {
    console.warn('setRestaurantStorageItem: restaurantSlug is required');
    return;
  }
  try {
    const scopedKey = getRestaurantStorageKey(restaurantSlug, key);
    localStorage.setItem(scopedKey, encryptStorageValue(value));
  } catch (error) {
    console.error(`Error setting restaurant storage item for ${key}:`, error);
  }
};

/**
 * Remove item from restaurant-scoped localStorage
 * @param {string} restaurantSlug - The restaurant slug
 * @param {string} key - The base key name
 */
export const removeRestaurantStorageItem = (restaurantSlug, key) => {
  if (!restaurantSlug) {
    return;
  }
  try {
    const scopedKey = getRestaurantStorageKey(restaurantSlug, key);
    localStorage.removeItem(scopedKey);
  } catch (error) {
    console.error(`Error removing restaurant storage item for ${key}:`, error);
  }
};

/** Normalized location id for logo cache keys (parent / no branch → "0") */
export const getLogoCacheLocationSuffix = (locationId) => {
  if (locationId === null || locationId === undefined || locationId === '') {
    return '0';
  }
  const n = typeof locationId === 'number' ? locationId : parseInt(String(locationId), 10);
  if (Number.isFinite(n) && n > 0) {
    return String(n);
  }
  return '0';
};

const companyLogoStorageKey = (locationId) =>
  `company_logo_${getLogoCacheLocationSuffix(locationId)}`;

/**
 * Read cached company logo (raw path from API). Resolve URL at display time with resolveImageUrl().
 * @returns {{ rawLogo: string, savedAt: number } | null}
 */
export const readCachedCompanyLogo = (restaurantSlug, locationId) => {
  if (!restaurantSlug) return null;
  try {
    const data = getRestaurantStorageItem(restaurantSlug, companyLogoStorageKey(locationId));
    if (data && typeof data.rawLogo === 'string' && data.rawLogo.trim() !== '') {
      return { rawLogo: data.rawLogo.trim(), savedAt: data.savedAt || 0 };
    }
  } catch (_) {
    /* ignore */
  }
  return null;
};

/**
 * Persist company logo path for instant header paint on return visits.
 * @param {string} restaurantSlug
 * @param {number|null|undefined} locationId
 * @param {{ rawLogo: string }} payload
 */
export const writeCachedCompanyLogo = (restaurantSlug, locationId, payload) => {
  if (!restaurantSlug || !payload?.rawLogo?.trim()) return;
  setRestaurantStorageItem(restaurantSlug, companyLogoStorageKey(locationId), {
    rawLogo: payload.rawLogo.trim(),
    savedAt: Date.now(),
  });
};

export const removeCachedCompanyLogo = (restaurantSlug, locationId) => {
  if (!restaurantSlug) return;
  removeRestaurantStorageItem(restaurantSlug, companyLogoStorageKey(locationId));
};

/**
 * Clear all storage for a specific restaurant
 * @param {string} restaurantSlug - The restaurant slug
 */
export const clearRestaurantStorage = (restaurantSlug) => {
  if (!restaurantSlug) {
    return;
  }
  try {
    const prefix = getRestaurantStorageKey(restaurantSlug, '');
    const keysToRemove = [];
    
    // Find all keys that start with the restaurant prefix
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    
    // Remove all matching keys
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.error(`Error clearing restaurant storage for ${restaurantSlug}:`, error);
  }
};

